import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function SpreadsheetArticle() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <nav className="mb-6 text-sm text-foreground-500">
            <button
              onClick={() => navigate('/')}
              className="hover:text-primary-600 transition-colors"
            >
              {t('nav.home')}
            </button>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">AllChinaBuy Spreadsheet</span>
          </nav>

          <header className="mb-10 md:mb-14">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              AllChinaBuy Spreadsheet Complete Guide 2026
            </h1>
            <p className="text-sm text-foreground-500">
              Published: August 2026 · Updated: August 2026 · Reading time: 12 min
            </p>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-6 md:space-y-8">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                What Is an AllChinaBuy Spreadsheet?
              </h2>
              <p>
                An <strong>AllChinaBuy spreadsheet</strong> is a structured digital resource that helps buyers organize, compare, and discover products across the AllChinaBuy marketplace. Instead of browsing randomly through hundreds of listings, a spreadsheet brings together product links, seller details, pricing information, and quality notes into a single, easy-to-scan format. Whether you are looking for sneakers, clothing, accessories, or electronics, the right spreadsheet can save hours of research time.
              </p>
              <p>
                For anyone serious about shopping efficiently through AllChinaBuy, building or using a curated spreadsheet is one of the smartest moves you can make. It transforms scattered product information into a clear, actionable buying plan.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                How the AllChinaBuy Spreadsheet Works
              </h2>
              <p>
                A well-organized AllChinaBuy spreadsheet typically includes columns for product name, category, seller name, product link, price range, size availability, color options, and personal notes. Advanced users may also add columns for QC photo links, shipping method preferences, and order status tracking. This turns your shopping list into a powerful project management tool.
              </p>
              <p>
                The core principle is simple: collect information before you buy, not after. By documenting products in a spreadsheet, you can compare prices across sellers, check which items have been reviewed by other buyers, and avoid duplicate purchases. It is especially useful for group buys or when building a seasonal wardrobe.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                How to Find Products Using the Spreadsheet
              </h2>
              <p>
                Start by defining what you need. Are you looking for a specific brand, style, or budget range? Write down your criteria before opening the spreadsheet. Then use the filter and search functions to narrow down options. Most spreadsheets allow sorting by category, price, or seller rating, making it easy to find exactly what fits your needs.
              </p>
              <p>
                When you find a product that looks promising, click through to the product page to verify details. The spreadsheet is a starting point, not a replacement for due diligence. Always double-check sizes, materials, and return policies before adding anything to your cart.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                How to Find Reliable Sellers
              </h2>
              <p>
                Not all sellers on AllChinaBuy offer the same quality or service. A good spreadsheet should include seller ratings, review counts, and specialization notes. Look for sellers with high repeat customer rates and positive feedback on product accuracy. Avoid sellers with frequent complaints about wrong sizes, poor materials, or slow shipping.
              </p>
              <p>
                Cross-reference seller information across multiple sources. Check community forums, Reddit discussions, and review aggregators. A seller that appears consistently in well-maintained spreadsheets is usually a safer bet than one you find only once.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                How to Check Product Information
              </h2>
              <p>
                Before buying, verify the product details in the spreadsheet against the actual listing page. Check for updated photos, accurate sizing charts, and material descriptions. If the spreadsheet lists a product as "cotton" but the product page says "polyester blend," that is a red flag worth investigating.
              </p>
              <p>
                Pay attention to batch information. Some products change materials or production details between batches. A spreadsheet that notes batch dates or version numbers helps you avoid surprises. When in doubt, message the seller directly for clarification.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                How to Read QC Photos
              </h2>
              <p>
                Quality control (QC) photos are your best defense against receiving a disappointing product. When your item arrives at the warehouse, inspect the QC photos carefully before approving shipment. Look for stitching quality, logo placement, color accuracy, and material texture. Compare the photos against the original product listing.
              </p>
              <p>
                Common issues to watch for include misaligned logos, loose threads, incorrect colors, and size discrepancies. If something looks off, request additional photos or consider exchanging the item. It is far easier to fix problems before the package leaves the warehouse than after it arrives at your door.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                AllChinaBuy Shipping Explained
              </h2>
              <p>
                Shipping is a critical part of the AllChinaBuy experience. The platform offers multiple shipping methods ranging from budget-friendly postal services to fast courier options. Your choice depends on your budget, timeline, and location. Always factor in shipping costs when comparing product prices, as they can significantly impact the total cost.
              </p>
              <p>
                Consider insurance for high-value items. While it adds cost, it provides peace of mind if a package is lost or damaged. Track your shipment closely and be prepared for customs clearance if you are ordering internationally. Some methods include customs handling, while others require you to manage it yourself.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                AllChinaBuy Warehouse Guide
              </h2>
              <p>
                The AllChinaBuy warehouse acts as a middle station between sellers and buyers. When you place an order, the seller ships to the warehouse first. This allows the warehouse team to inspect items, take QC photos, and consolidate multiple packages into one shipment. Understanding how the warehouse works helps you plan your purchases better.
              </p>
              <p>
                Storage time at the warehouse is usually limited. If you do not ship your items within the free storage period, additional fees may apply. Plan your purchases so you can consolidate and ship within the allowed window. Batch your orders together to save on shipping costs.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                AllChinaBuy Safety and Security
              </h2>
              <p>
                Shopping safely on AllChinaBuy requires the same precautions as any online marketplace. Use strong, unique passwords for your account. Enable two-factor authentication if available. Never share your login credentials with anyone, and be cautious of phishing attempts that impersonate AllChinaBuy communications.
              </p>
              <p>
                Use secure payment methods and avoid direct bank transfers to sellers. AllChinaBuy offers buyer protection when you pay through the platform. If a seller asks for payment outside the system, that is a major red flag. Stay within the platform for the safest transaction experience.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Common Mistakes to Avoid
              </h2>
              <p>
                New buyers often make a few common mistakes. The first is skipping QC photos. Always review them before shipping. The second is ignoring sizing charts. Asian sizing often differs from Western standards, so measure yourself and compare carefully. The third is not budgeting for shipping costs. A cheap product can become expensive once shipping is added.
              </p>
              <p>
                Another mistake is buying too much at once. Start with a small test order to evaluate a seller's quality and service before placing larger orders. This minimizes risk and helps you learn the platform without committing a large amount of money upfront.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Spreadsheet Tips for Better Shopping
              </h2>
              <p>
                Keep your spreadsheet updated. Mark items as "ordered," "in warehouse," "shipped," or "received." Add notes about seller quality, fit, and material so you can make better decisions next time. Share your spreadsheet with trusted friends for group buys, but keep sensitive information private.
              </p>
              <p>
                Use color coding to highlight priority items, deals, or warnings. A well-maintained spreadsheet becomes a personal knowledge base that improves with every purchase. Over time, you will build a reliable database of trusted sellers, quality products, and smart buying strategies.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Product Categories Overview
              </h2>
              <p>
                AllChinaBuy spreadsheets cover a wide range of product categories. The most popular include shoes, hoodies, t-shirts, jackets, pants, headwear, accessories, bags, belts, and perfumes. Each category has its own set of trusted sellers, sizing considerations, and quality benchmarks. Specialized spreadsheets exist for niche categories like jerseys, sets, and underwear.
              </p>
              <p>
                When building or using a category-specific spreadsheet, focus on the factors that matter most for that product type. For shoes, sizing accuracy and material quality are critical. For clothing, fabric composition and stitching matter most. For accessories, logo accuracy and hardware quality should be prioritized.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Buying Checklist
              </h2>
              <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Define your budget and product requirements before searching</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Verify seller ratings and review history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Check product details against the listing page</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Review QC photos carefully before shipping</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Compare shipping methods and costs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Factor in customs and duties for international orders</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                    <span>Keep records of all orders and communications</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">Is the AllChinaBuy spreadsheet free to use?</h3>
                  <p className="text-sm">Most community-maintained spreadsheets are free. Some premium or highly curated lists may require access requests or small contributions. The value comes from how well the spreadsheet is maintained and updated, not the price.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">How often should I update my spreadsheet?</h3>
                  <p className="text-sm">Update your spreadsheet every time you place an order, receive QC photos, or complete a purchase. For shared spreadsheets, update them at least once per week to keep information accurate for the community.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">Can I trust all sellers in a spreadsheet?</h3>
                  <p className="text-sm">No. Even well-maintained spreadsheets cannot guarantee seller quality. Always verify current seller ratings, read recent reviews, and start with small test orders before making large purchases.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">What is the best shipping method for beginners?</h3>
                  <p className="text-sm">For beginners, tracked postal services with reasonable delivery times are usually the best balance of cost and reliability. Avoid the cheapest untracked options until you understand the platform better.</p>
                </div>
              </div>
            </section>

            <div className="bg-primary-50 rounded-lg p-5 md:p-6 border border-primary-200/70 mt-10">
              <p className="text-sm text-foreground-700 mb-4">
                <strong>Disclaimer:</strong> This website is an independent informational resource. Information can change and should be independently verified before making purchasing decisions. We are not affiliated with AllChinaBuy or any sellers mentioned. Always verify current prices, shipping rates, and policies directly on the platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Explore AllChinaBuy Spreadsheet
                  <i className="ri-arrow-right-line" />
                </a>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-background-50 text-foreground-700 font-medium text-sm rounded-md border border-background-200 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-line" />
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}